<template>
  <div class="needs-container">
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-content">
        <div class="title-section">
          <h1 class="page-title">
            <v-icon class="title-icon" color="#fff">mdi-hand-heart</v-icon>
            {{ $t("volunteer_opportunities") }}
          </h1>
        </div>

        <!-- Status Filter & Action Buttons -->
        <div class="action-buttons">
          <!-- Status Filter Dropdown -->
          <v-select v-model="statusFilter" :items="statusFilterOptions" label="Status" prepend-inner-icon="mdi-filter"
            outlined dense hide-details @change="onStatusFilterChange" class="status-filter-dropdown" dark
            style="max-width: 200px;" />

          <!-- Agencies Participated Button (Admin Only) -->
          <v-btn v-if="profileType === 'Admin'" color="white" elevation="2" large @click="onAgenciesParticipated"
            class="action-btn ml-3 agencies-btn">
            <v-icon left color="#084468">mdi-office-building-outline</v-icon>
            <span style="color: #084468;">Agencies Participated: {{ totalAgencies }}</span>
          </v-btn>

          <v-btn v-if="profileType === 'Agency'" color="primary" elevation="2" large @click="onCreateNeed"
            class="action-btn">
            <v-icon left>mdi-plus</v-icon>
            Create Need
          </v-btn>

          <v-btn v-if="profileType === 'Agency'" color="success" elevation="2" large @click="onReport"
            class="action-btn ml-3">
            <v-icon left>mdi-chart-line</v-icon>
            Reports
          </v-btn>

          <v-btn color="primary" elevation="2" large class="action-btn ml-3" @click="toggleFilters">
            <v-icon left>{{ showFilters ? 'mdi-filter-variant-remove' : 'mdi-filter-variant' }}</v-icon>
            {{ showFilters ? "Hide Filters" : "Show Filters" }}
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Active Filters Summary - Always Visible -->
    <!-- Active Filters Summary - Always Visible -->
    <div v-if="hasActiveFilters" class="active-filters-summary">
      <v-card class="filters-summary-card" elevation="2">
        <v-card-text class="d-flex align-center flex-wrap pa-3">
          <v-icon left color="primary" class="mr-2">mdi-filter-variant</v-icon>
          <span class="filter-label font-weight-medium mr-2">Active Filters:</span>

          <!-- Search Filter -->
          <v-chip v-if="searchQuery" close @click:close="clearFilter('search')" color="primary" text-color="white" small
            class="mr-2 mb-1">
            <v-icon left small>mdi-magnify</v-icon>
            "{{ searchQuery }}"
          </v-chip>

          <!-- Date Range Filter - Read Only Display -->
          <v-chip v-if="dateRange.length === 2" close @click:close="clearFilter('date')" color="success"
            text-color="white" small class="mr-2 mb-1">
            <v-icon left small>mdi-calendar</v-icon>
            {{ formatDateRange }}
          </v-chip>

          <!-- Need Type Filter -->
          <v-chip v-if="selectedNeedType" close @click:close="clearFilter('type')" color="info" text-color="white" small
            class="mr-2 mb-1">
            <v-icon left small>mdi-shape</v-icon>
            {{ selectedNeedType }}
          </v-chip>

          <!-- Status Filter -->
          <v-chip v-if="selectedStatus && selectedStatus !== 'all'" close @click:close="clearFilter('status')"
            color="warning" text-color="white" small class="mr-2 mb-1">
            <v-icon left small>mdi-information</v-icon>
            {{ selectedStatus }}
          </v-chip>

          <!-- My Responses Filter -->
          <v-chip v-if="filterResponded && profileType === 'Volunteer'" close @click:close="clearFilter('response')"
            color="purple" text-color="white" small class="mr-2 mb-1">
            <v-icon left small>mdi-account-check</v-icon>
            My Responses
          </v-chip>

          <!-- Clear All Button -->
          <v-btn text small color="error" @click="clearAllFilters" class="clear-all-btn ml-2">
            <v-icon left small>mdi-close-circle</v-icon>
            Clear All
          </v-btn>

          <!-- Results Count -->
          <v-spacer></v-spacer>
          <v-chip color="primary" outlined small class="results-count-chip">
            <v-icon left small>mdi-format-list-bulleted</v-icon>
            {{ filteredNeeds.length }} result{{ filteredNeeds.length !== 1 ? 's' : '' }}
          </v-chip>

          <!-- Quick Toggle Filters Button -->
          <v-btn text small color="primary" @click="toggleFilters" class="ml-2 toggle-filters-btn">
            <v-icon left small>{{ showFilters ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
            {{ showFilters ? 'Hide' : 'Show' }}
          </v-btn>
        </v-card-text>
      </v-card>
    </div>

    <!-- Advanced Filters Section -->
    <div class="sticky-filters-wrapper" v-if="showFilters">
      <v-card class="filters-card" elevation="3">
        <v-card-title class="filters-title">
          <v-icon left color="#ffff">mdi-filter-variant</v-icon>
          Filter & Search
          <v-spacer></v-spacer>
          <v-btn text small color="white" @click="toggleFilters">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="mt-3">
          <v-row>
            <!-- Search Bar -->
            <v-col cols="12" md="4">
              <v-text-field v-model="searchQuery" label="Search needs..." prepend-inner-icon="mdi-magnify" outlined
                dense clearable @input="onSearch" class="search-field" />
            </v-col>

            <!-- Date Range Filter -->
            <v-col cols="12" md="4">
              <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y
                max-width="290px" min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="dateRangeText" label="Date Range" prepend-inner-icon="mdi-calendar" readonly
                    outlined dense v-bind="attrs" v-on="on" clearable @click:clear="clearFilter('date')" />
                </template>
                <v-date-picker v-model="dateRange" range @change="onDateRangeChange" />
              </v-menu>
            </v-col>

            <!-- Need Type Filter -->
            <v-col cols="12" md="4">
              <v-select v-model="selectedNeedType" :items="needTypes" label="Need Type" prepend-inner-icon="mdi-shape"
                outlined dense clearable @change="onTypeFilter" />
            </v-col>

            <!-- Volunteer Response Filter (for Volunteers only) -->
            <v-col v-if="profileType === 'Volunteer'" cols="12" md="12">
              <v-switch v-model="filterResponded" label="Show only needs I've responded to" color="primary"
                @change="onChangeFilter" class="response-switch" />
            </v-col>
          </v-row>

          <!-- Sort Options - Added here -->
          <v-row class="mt-4">
            <v-col cols="12" md="6" lg="4">
              <v-select v-model="sortBy" :items="sortOptions" label="Sort by" prepend-inner-icon="mdi-sort" outlined
                dense hide-details @change="onSort" />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-section">
      <v-skeleton-loader v-for="n in 6" :key="n" type="card" class="mb-4" />
    </div>

    <!-- Needs Grid -->
    <div v-else class="needs-grid">
      <transition-group name="fade" tag="div" class="needs-container-grid">
        <enhanced-need-card v-for="need in paginatedNeeds" :key="need._id" :need="need" :profile-type="profileType"
          @edit="onEdit" @delete="onDelete" @share="onShare" @details="goToDetails" class="need-card-item" />
      </transition-group>
    </div>

    <!-- Pagination -->
    <div v-if="shouldShowPagination" class="pagination-section">
      <v-pagination v-model="currentPage" :length="totalPages" :total-visible="7" color="primary"
        class="custom-pagination" @input="onPageChange" />
    </div>

    <!-- Empty State -->
    <v-card v-if="!loading && filteredNeeds.length === 0" class="empty-state-card" elevation="3">
      <v-card-text class="text-center pa-8">
        <v-img :src="emptyStateImage" height="200" contain class="mb-4" />
        <h3 class="empty-title mb-3">{{ emptyStateTitle }}</h3>
        <p class="empty-subtitle mb-4">{{ emptyStateSubtitle }}</p>
        <v-btn v-if="profileType === 'Agency'" color="primary" large @click="onCreateNeed">
          <v-icon left>mdi-plus</v-icon>
          Create Your First Need
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Dialogs -->
    <agency-create-need :dialog="createDialog" :onCloseDialog="onCloseCreateDialog" />

    <agency-edit-need :item="selected" :dialog="editDialog" :onCloseDialog="onCloseEditDialog"
      v-if="selected && editDialog" />

    <confirm-delete-need-dialog :deleteDialog="deleteDialog" :need="selected" :onClose="onCloseConfirmDelete"
      :onDeleted="onDeletedNeed" />

    <!-- Enhanced Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="snackbarTimeout" top right elevation="6">
      <v-icon left color="white">{{ snackbarIcon }}</v-icon>
      {{ snackMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text color="white" v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import ConfirmDeleteNeedDialog from "../../../components/common/ConfirmDeleteNeedDialog.vue";
import AgencyCreateNeed from "./create/AgencyCreateNeed.vue";
import AgencyEditNeed from "./create/AgencyEditNeed.vue";
import EnhancedNeedCard from "././AgencyNeedCard.vue";

export default {
  name: "NeedsFeed",
  components: {
    AgencyCreateNeed,
    ConfirmDeleteNeedDialog,
    AgencyEditNeed,
    EnhancedNeedCard
  },
  data() {
    return {
      loading: false,
      createDialog: false,
      editDialog: false,
      deleteDialog: false,
      selected: null,
      showFilters: false,

      // Snackbar
      snackbar: false,
      snackMessage: null,
      snackbarColor: "success",
      snackbarIcon: "mdi-check",
      snackbarTimeout: 4000,

      // Filters
      searchQuery: "",
      dateRange: [],
      dateMenu: false,
      selectedNeedType: null,
      selectedStatus: null,
      filterResponded: false,
      statusFilter: "all",

      // Sorting & Pagination
      sortBy: "newest",
      currentPage: 1,
      itemsPerPage: 12,

      dateRangeError: "",
      dateValidationMessage: "End date must be after start date",

      // Filter Options
      needTypes: [
        "Is Ongoing",
        "Happens On",
        "Runs Until",
        "Multi-Date",
        "Custom Shifts",
        "Recurring Shifts"
      ],

      statusFilterOptions: [
        { text: "All", value: "all" },
        { text: "Active", value: "upcoming" },
        { text: "Completed", value: "completed" }
      ],

      sortOptions: [
        { text: "Newest First", value: "newest" },
        { text: "Oldest First", value: "oldest" },
        { text: "Deadline Soon", value: "deadline" },
        { text: "Most Urgent", value: "urgent" },
        { text: "Alphabetical", value: "alphabetical" }
      ],

      // Internal state
      filteredNeeds: []
    };
  },

  computed: {
    ...mapGetters("need", {
      needs: "getAllNeeds",
      adminNeeds: "getAllAdminNeeds"
    }),

    ...mapState("auth", {
      profile: "profile",
      profileType: "type"
    }),

    cardNeeds() {
      if (this.profileType === "Admin") {
        return this.adminNeeds?.res || this.adminNeeds || [];
      }
      return this.needs;
    },

    totalAgencies() {
      if (this.profileType === "Admin" && this.adminNeeds) {
        return this.adminNeeds.totalAgencies || 0;
      }
      return 0;
    },

    dateRangeText() {
      if (this.dateRange.length === 2) {
        return `${this.formatDate(this.dateRange[0])} - ${this.formatDate(this.dateRange[1])}`;
      }
      return "";
    },

    formatDateRange() {
      if (this.dateRange.length === 2) {
        return `${this.formatDate(this.dateRange[0])} - ${this.formatDate(this.dateRange[1])}`;
      }
      return "";
    },

    hasActiveFilters() {
      return !!(
        this.searchQuery ||
        this.dateRange.length === 2 ||
        this.selectedNeedType ||
        this.selectedStatus ||
        this.filterResponded
      );
    },

    paginatedNeeds() {
      if (this.profileType === "Admin") {
        return this.filteredNeeds;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredNeeds.slice(start, end);
    },

    totalPages() {
      if (this.profileType === "Admin" && this.adminNeeds?.total) {
        return Math.ceil(this.adminNeeds.total / this.itemsPerPage);
      }
      return Math.ceil(this.filteredNeeds.length / this.itemsPerPage) || 1;
    },

    shouldShowPagination() {
      if (this.profileType === "Admin" && this.adminNeeds?.total) {
        return this.adminNeeds.total > this.itemsPerPage;
      }
      return this.filteredNeeds.length > this.itemsPerPage;
    },

    emptyStateImage() {
      return require("@/assets/svg/no-need.svg");
    },

    emptyStateTitle() {
      if (this.hasActiveFilters) {
        return "No matching needs found";
      }
      return this.profileType === "Agency"
        ? "No needs created yet"
        : "No opportunities available";
    },

    emptyStateSubtitle() {
      if (this.hasActiveFilters) {
        return "Try adjusting your filters to find more opportunities";
      }
      return this.profileType === "Agency"
        ? "Create your first need and give volunteers opportunities to help you"
        : "Check back later for new volunteer opportunities";
    }
  },

  methods: {
    ...mapActions("need", {
      fetchAllNeeds: "fetchAllNeeds",
      fetchAllAdminNeeds: "fetchAllAdminNeeds"
    }),

    // Core Actions
    onCreateNeed() {
      this.createDialog = true;
    },

    onCloseCreateDialog() {
      this.createDialog = false;
      this.refreshData();
    },

    goToDetails(item) {
      this.$router.push({
        name: "agency-need-details",
        query: { _id: item._id }
      });
    },

    onDelete(need) {
      this.selected = need;
      this.deleteDialog = true;
    },

    onDeletedNeed() {
      this.deleteDialog = false;
      this.showSnackbar("Need deleted successfully!", "error", "mdi-delete");
      this.refreshData();
    },

    onEdit(need) {
      this.selected = need;
      this.editDialog = true;
    },

    onCloseEditDialog() {
      this.editDialog = false;
      this.refreshData();
      this.showSnackbar("Need updated successfully!", "info", "mdi-pencil");
    },

    onCloseConfirmDelete() {
      this.deleteDialog = false;
    },

    onReport() {
      this.$router.push({ name: "agency-report" });
    },

    onAgenciesParticipated() {
      this.showSnackbar(
        `Total Agencies Participated: ${this.totalAgencies}`,
        "info",
        "mdi-office-building"
      );
    },

    async onShare(need) {
      const url = `${process.env.VUE_APP_WEB_URL}/needs/details?_id=${need._id}`;
      await this.copyToClipboard(url);
      this.showSnackbar("Link copied to clipboard!", "info", "mdi-link");
    },

    // Filter Management
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },

    // Method to restrict allowed dates (optional)
    allowedDates(date) {
      // If date range has a start date, prevent selecting dates before it
      if (this.dateRange.length === 1) {
        const startDate = new Date(this.dateRange[0]);
        const selectedDate = new Date(date);
        // Prevent selecting dates before the start date when only start is selected
        return selectedDate >= startDate;
      }
      return true;
    },


    clearFilter(type) {
      switch (type) {
        case "search":
          this.searchQuery = "";
          this.onSearch();
          break;
        case "date":
          this.dateRange = [];
          this.dateRangeError = "";
          this.applyFilters();
          break;
        case "type":
          this.selectedNeedType = null;
          this.onTypeFilter();
          break;
        case "status":
          this.selectedStatus = null;
          this.onStatusFilter();
          break;
        case "response":
          this.filterResponded = false;
          this.onChangeFilter();
          break;
        default:
          break;
      }
    },

    clearAllFilters() {
      this.searchQuery = "";
      this.dateRange = [];
      this.selectedNeedType = null;
      this.selectedStatus = null;
      this.filterResponded = false;
      this.currentPage = 1;
      this.applyFilters();
    },

    // Filtering Methods
    onSearch() {
      this.currentPage = 1;
      this.applyFilters();
    },

    onDateRangeChange() {
      // Validate the date range
      if (this.dateRange.length === 2) {
        const startDate = new Date(this.dateRange[0]);
        const endDate = new Date(this.dateRange[1]);

        // Check if end date is before start date
        if (endDate < startDate) {
          this.dateRangeError = this.dateValidationMessage;
          // Reset the date range or handle error
          this.showSnackbar("End date cannot be before start date!", "error", "mdi-calendar-alert");
          // Optionally clear the selection or swap dates
          this.dateRange = [];
          this.dateMenu = false;
          return;
        } else {
          this.dateRangeError = "";
          this.dateMenu = false;
          this.currentPage = 1;
          this.applyFilters();
        }
      }
    },
    // New method to validate dates as user selects
    validateDateRange() {
      if (this.dateRange.length === 2) {
        const startDate = new Date(this.dateRange[0]);
        const endDate = new Date(this.dateRange[1]);

        if (endDate < startDate) {
          this.dateRangeError = this.dateValidationMessage;
          return false;
        } else {
          this.dateRangeError = "";
          return true;
        }
      }
      return true;
    },


    onTypeFilter() {
      this.currentPage = 1;
      this.applyFilters();
    },

    onStatusFilter() {
      this.currentPage = 1;
      this.applyFilters();
    },

    onChangeFilter() {
      this.currentPage = 1;
      this.applyFilters();
    },

    onPageChange(page) {
      if (this.profileType === "Admin") {
        this.loading = true;
        const payload = { type: this.statusFilter, offset: page };
        this.fetchAllAdminNeeds(payload)
          .then(() => {
            this.loading = false;
            this.applyFilters();
            window.scrollTo({ top: 0, behavior: "smooth" });
          })
          .catch(() => {
            this.loading = false;
            this.showSnackbar("Error loading needs", "error", "mdi-alert");
          });
      }
    },

    onStatusFilterChange() {
      this.currentPage = 1;
      const type = this.statusFilter;

      this.$router.push({
        path: "/needs/feeds",
        query: type !== "all" ? { type } : {}
      });

      this.loading = true;
      if (this.profileType === "Admin") {
        this.fetchAllAdminNeeds({ type, offset: 1 })
          .then(() => {
            this.loading = false;
            this.applyFilters();
          })
          .catch(() => {
            this.loading = false;
            this.showSnackbar("Error loading needs", "error", "mdi-alert");
          });
      } else {
        this.fetchAllNeeds()
          .then(() => {
            this.loading = false;
            this.applyFilters();
          })
          .catch(() => {
            this.loading = false;
            this.showSnackbar("Error loading needs", "error", "mdi-alert");
          });
      }
    },

    applyFilters() {
      let filtered = [...this.cardNeeds];

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          need =>
            need.title?.toLowerCase().includes(query) ||
            need.description?.toLowerCase().includes(query) ||
            need.organization?.toLowerCase().includes(query)
        );
      }

      // Date range filter - only apply if valid
      if (this.dateRange.length === 2) {
        const startDate = new Date(this.dateRange[0]);
        const endDate = new Date(this.dateRange[1]);
        endDate.setHours(23, 59, 59, 999);

        // Double-check validation
        if (endDate >= startDate) {
          filtered = filtered.filter(need => {
            if (!need.createdAt) return false;
            const needDate = new Date(need.createdAt);
            return needDate >= startDate && needDate <= endDate;
          });
        }
      }

      // Need type filter
      if (this.selectedNeedType) {
        filtered = filtered.filter(
          need =>
            need.duration === this.selectedNeedType ||
            need.type === this.selectedNeedType ||
            need.category === this.selectedNeedType
        );
      }

      // Status filter
      if (this.selectedStatus && this.selectedStatus !== "all") {
        filtered = filtered.filter(need => need.status === this.selectedStatus);
      }

      // Response filter for volunteers
      if (this.profileType === "Volunteer" && this.filterResponded) {
        filtered = filtered.filter(need => need.response != null);
      }

      this.filteredNeeds = filtered;
      this.sortNeeds();
    },

    // Sorting
    onSort() {
      this.sortNeeds();
    },

    sortNeeds() {
      const sortFunctions = {
        newest: () => { },
        oldest: () => this.filteredNeeds.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        ),
        deadline: () => this.filteredNeeds.sort(
          (a, b) => new Date(a.deadline) - new Date(b.deadline)
        ),
        urgent: () => this.filteredNeeds.sort(
          (a, b) => (b.priority || 0) - (a.priority || 0)
        ),
        alphabetical: () => this.filteredNeeds.sort(
          (a, b) => a.title.localeCompare(b.title)
        )
      };

      if (sortFunctions[this.sortBy]) {
        sortFunctions[this.sortBy]();
      }
    },

    // Utility Methods
    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },

    showSnackbar(message, color = "success", icon = "mdi-check") {
      this.snackMessage = message;
      this.snackbarColor = color;
      this.snackbarIcon = icon;
      this.snackbar = true;
    },

    async copyToClipboard(textToCopy) {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(textToCopy);
      }

      const textArea = document.createElement("textarea");
      textArea.value = textToCopy;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      return new Promise((resolve, reject) => {
        try {
          document.execCommand("copy");
          resolve();
        } catch (err) {
          reject(err);
        } finally {
          textArea.remove();
        }
      });
    },

    checkApproved() {
      if (this.profile?.status === "Pending" || this.profile?.status === "Rejected") {
        this.$router.push({ name: "agency-approval" });
      }
    },

    refreshData() {
      if (this.profileType === "Admin") {
        this.fetchAllAdminNeeds({ type: this.statusFilter, offset: this.currentPage });
      } else {
        this.fetchAllNeeds();
      }
    },

    loadData() {
      this.loading = true;
      const urlParams = new URLSearchParams(window.location.search);
      const type = urlParams.get("type") || "all";
      this.statusFilter = type;

      const fetchPromise = this.profileType === "Admin"
        ? this.fetchAllAdminNeeds({ type, offset: 1 })
        : this.fetchAllNeeds();

      fetchPromise
        .then(() => {
          this.loading = false;
          this.applyFilters();
        })
        .catch(() => {
          this.loading = false;
          this.showSnackbar("Error loading needs", "error", "mdi-alert");
        });
    }
  },

  watch: {
    profile: {
      handler() {
        if (this.profile) {
          this.checkApproved();
        }
      },
      immediate: true
    },

    dateRange: {
      handler(newVal) {
        if (newVal.length === 2) {
          this.validateDateRange();
        }
      },
      deep: true
    },

    cardNeeds: {
      handler() {
        this.applyFilters();
      },
      deep: true
    }
  },

  mounted() {
    this.loadData();
  },

  beforeDestroy() {
    // Clean up any subscriptions or listeners if needed
  }
};
</script>

<style scoped>
/* Active Filters Summary - Always Visible */
.active-filters-summary {
  margin-bottom: 20px;
  animation: slideDown 0.3s ease;
}

.filters-summary-card {
  border-radius: 12px !important;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-left: 4px solid #084468;
  transition: all 0.3s ease;
}

.filters-summary-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1) !important;
}

.filters-summary-card .v-card__text {
  padding: 12px 16px !important;
}

.filter-label {
  color: #2c3e50;
  font-size: 0.9rem;
}

.results-count-chip {
  background: #f5f7fa !important;
}

.clear-all-btn {
  text-transform: none !important;
  font-weight: 600 !important;
}

.toggle-filters-btn {
  text-transform: none !important;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Main Container */
.needs-container {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
}

/* Header Section */
.header-section {
  margin-bottom: 16px;
  padding: 20px;
  border-radius: 20px;
  background: linear-gradient(135deg, #084468, #0a5b8a);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
}

.title-section {
  flex: 1;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffff;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.title-icon {
  font-size: 2.5rem !important;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.action-btn {
  border-radius: 12px !important;
  text-transform: none;
  font-weight: 600;
}

.status-filter-dropdown {
  min-width: 180px;
}

.status-filter-dropdown:deep(.v-input__control) {
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
}

.status-filter-dropdown:deep(.v-input__slot) {
  background: rgba(255, 255, 255, 0.2) !important;
}

.status-filter-dropdown:deep(.v-label) {
  color: white !important;
  font-weight: 600;
}

.status-filter-dropdown:deep(.v-select__selection) {
  font-weight: 600;
  color: white !important;
}

.status-filter-dropdown:deep(.v-icon) {
  color: white !important;
}

.status-filter-dropdown:deep(fieldset) {
  border-color: rgba(255, 255, 255, 0.5) !important;
}

.status-filter-dropdown:deep(.v-input--is-focused fieldset) {
  border-color: white !important;
  border-width: 2px !important;
}

.agencies-btn {
  font-weight: 700 !important;
  box-shadow: 0 4px 12px rgba(8, 68, 104, 0.3) !important;
  transition: all 0.3s ease !important;
}

.agencies-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(8, 68, 104, 0.4) !important;
}

/* Filters Section */
.sticky-filters-wrapper {
  position: sticky;
  top: 0;
  z-index: 100;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  margin-bottom: 16px;
  transition: all 0.3s ease;
  border-radius: 20px;
}

.sticky-filters-wrapper .filters-card {
  margin-bottom: 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.filters-card {
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
}

.filters-title {
  background: linear-gradient(135deg, #084468, #0a5b8a);
  color: white;
  margin: 0;
  border-radius: 16px 16px 0 0;
}

.search-field {
  border-radius: 12px;
}

.response-switch {
  margin-top: 8px;
}

/* Loading State */
.loading-section {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
}

/* Needs Grid */
.needs-grid {
  margin-bottom: 32px;
}

.needs-container-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
}

.need-card-item {
  transition: all 0.3s ease;
}

.need-card-item:hover {
  transform: translateY(-4px);
}

/* Animations */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Pagination */
.pagination-section {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

.custom-pagination {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  padding: 12px;
}

/* Empty State */
.empty-state-card {
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
}

.empty-title {
  color: #2c3e50;
  font-weight: 600;
}

.empty-subtitle {
  color: #7f8c8d;
  font-size: 1.1rem;
}

/* Responsive Design */
@media (max-width: 960px) {
  .needs-container {
    padding: 16px;
  }

  .page-title {
    font-size: 2rem;
  }

  .header-content {
    flex-direction: column;
    align-items: stretch;
  }

  .action-buttons {
    justify-content: center;
  }

  .needs-container-grid {
    grid-template-columns: 1fr;
  }

  .needs-container-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

@media (max-width: 600px) {
  .page-title {
    font-size: 1.75rem;
    flex-direction: column;
    gap: 8px;
  }

  .title-icon {
    font-size: 2rem !important;
  }

  .action-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .action-btn {
    width: 100%;
  }

  .status-filter-dropdown {
    max-width: 100% !important;
    width: 100%;
  }

  .filters-summary-card .v-card__text {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 8px;
  }

  .filters-summary-card .v-card__text .v-chip {
    width: 100%;
    justify-content: center;
  }

  .filters-summary-card .v-card__text .v-spacer {
    display: none;
  }

  .clear-all-btn {
    width: 100%;
    margin-left: 0 !important;
  }

  .toggle-filters-btn {
    width: 100%;
    margin-left: 0 !important;
  }

  .needs-container-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 960px) {
  .sticky-filters-wrapper {
    top: -1px;
  }
}

:deep(.v-input--is-error .v-input__control) {
  border-color: #ff5252 !important;
}

:deep(.v-messages__message) {
  color: #ff5252;
  font-weight: 500;
}

/* Optional: Style for error state in date picker */
:deep(.v-picker--date) {
  .v-date-picker-table .v-btn--active {
    background-color: #084468 !important;
  }

  .v-date-picker-table .v-btn--disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}
</style>